import { useCallback, useEffect, useRef, useState } from "react";
import type { AdminAppObject, AdminSeries, I18nText, LibraryImage } from "@shared";
import { ApiFetchError } from "../api/client";
import * as api from "./adminApi";
import I18nFields from "./I18nFields";

interface Props {
  eventId: string;
  series: AdminSeries[];
  onChanged: () => void;
}

const emptyName: I18nText = { fr: "" };

export default function AppObjectsPanel({ eventId, series, onChanged }: Props) {
  const [seriesId, setSeriesId] = useState<string>(series[0]?.id ?? "");
  const [objects, setObjects] = useState<AdminAppObject[]>([]);
  const [library, setLibrary] = useState<LibraryImage[]>([]);
  const [nameI18n, setNameI18n] = useState<I18nText>(emptyName);
  const [descriptionI18n, setDescriptionI18n] = useState<I18nText | null>(null);
  const [sortOrder, setSortOrder] = useState(0);
  const [image, setImage] = useState<File | null>(null);
  const [libraryImageId, setLibraryImageId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);

  // La série sélectionnée peut disparaître (suppression) : on retombe sur la
  // première disponible plutôt que de rester sur un identifiant mort.
  useEffect(() => {
    if (series.find((s) => s.id === seriesId) === undefined) {
      setSeriesId(series[0]?.id ?? "");
    }
  }, [series, seriesId]);

  const refresh = useCallback(async () => {
    if (seriesId === "") {
      setObjects([]);
      return;
    }
    try {
      setObjects(await api.listAppObjects(seriesId));
    } catch {
      setError("Impossible de charger les objets.");
    }
  }, [seriesId]);

  const refreshLibrary = useCallback(async () => {
    try {
      setLibrary(await api.listImageLibrary(eventId));
    } catch {
      setLibrary([]);
    }
  }, [eventId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    void refreshLibrary();
  }, [refreshLibrary]);

  function reset(): void {
    setEditingId(null);
    setNameI18n(emptyName);
    setDescriptionI18n(null);
    setSortOrder(0);
    setImage(null);
    setLibraryImageId(null);
    if (fileInput.current !== null) fileInput.current.value = "";
  }

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    const input = { nameI18n, descriptionI18n, sortOrder, libraryImageId };

    try {
      if (editingId === null) await api.createAppObject(seriesId, input, image);
      else await api.updateAppObject(editingId, input, image);
      reset();
      await refresh();
      await refreshLibrary();
      onChanged();
    } catch (err) {
      if (err instanceof ApiFetchError && err.code === "image_too_large") {
        setError("Image trop lourde. Choisis un fichier plus léger.");
      } else if (err instanceof ApiFetchError && err.code === "image_bad_format") {
        setError("Format d'image non accepté. Utilise JPG, PNG ou WebP.");
      } else {
        setError("Enregistrement impossible. Le nom français est obligatoire.");
      }
    }
  }

  async function remove(item: AdminAppObject): Promise<void> {
    const confirmed = window.confirm(
      `Supprimer l'objet « ${api.fr(item.nameI18n)} » ?\n\n` +
        "ATTENTION : tous les codes qui désignaient cet objet comme bonne " +
        "réponse seront supprimés eux aussi. Action irréversible.",
    );
    if (!confirmed) return;
    await api.deleteAppObject(item.id);
    await refresh();
    await refreshLibrary();
    onChanged();
  }

  if (series.length === 0) {
    return (
      <section className="panel">
        <h2 className="panel__title">Objets de l'app</h2>
        <p className="panel__hint">Crée d'abord une série : un objet appartient à une série.</p>
      </section>
    );
  }

  return (
    <section className="panel">
      <h2 className="panel__title">Objets de l'app</h2>
      <p className="panel__hint">
        Ce que le jeune voit et doit deviner : une image, un nom, une description.
      </p>

      <label className="form__label" htmlFor="ao-series">
        Série
      </label>
      <select
        id="ao-series"
        className="form__input"
        value={seriesId}
        onChange={(e) => {
          setSeriesId(e.target.value);
          reset();
        }}
      >
        {series.map((s) => (
          <option key={s.id} value={s.id}>
            {s.icon !== null ? `${s.icon} ` : ""}
            {api.fr(s.nameI18n)}
          </option>
        ))}
      </select>

      {error !== null && <p className="form__error">{error}</p>}

      <ul className="list list--cards">
        {objects.map((item) => (
          <li key={item.id} className="list__item">
            {item.imageUrl !== null ? (
              <img className="thumb" src={item.imageUrl} alt="" />
            ) : (
              <div className="thumb thumb--empty" aria-hidden="true" />
            )}

            <div className="list__main list__main--static">
              <span className="list__name">{api.fr(item.nameI18n)}</span>
              <span className="list__meta">
                ordre {item.sortOrder}
                {item.nameI18n.en != null && ` · EN : ${item.nameI18n.en}`}
                {item.imageUrl === null && " · sans image"}
              </span>
            </div>

            <div className="list__actions">
              <button
                type="button"
                className="button button--small"
                onClick={() => {
                  setEditingId(item.id);
                  setNameI18n(item.nameI18n);
                  setDescriptionI18n(item.descriptionI18n);
                  setSortOrder(item.sortOrder);
                  setImage(null);
                  setLibraryImageId(null);
                  if (fileInput.current !== null) fileInput.current.value = "";
                }}
              >
                Modifier
              </button>
              <button
                type="button"
                className="button button--small button--danger"
                onClick={() => void remove(item)}
              >
                Supprimer
              </button>
            </div>
          </li>
        ))}
        {objects.length === 0 && <li className="list__empty">Aucun objet dans cette série.</li>}
      </ul>

      <form className="form" onSubmit={(e) => void submit(e)}>
        <h3 className="form__title">
          {editingId === null ? "Nouvel objet" : "Modifier l'objet"}
        </h3>

        <I18nFields id="ao-name" label="Nom de l'objet" value={nameI18n} onChange={setNameI18n} />

        <I18nFields
          id="ao-desc"
          label="Description (optionnelle)"
          value={descriptionI18n}
          onChange={setDescriptionI18n}
          multiline
          required={false}
        />

        {library.length > 0 && (
          <fieldset className="library">
            <legend className="form__label">
              Image du kit {editingId !== null && "(laisser vide pour conserver l'actuelle)"}
            </legend>
            <p className="panel__hint">
              Les {library.length} planches du kit physique, livrées avec l'application. Clique pour
              en choisir une — aucun téléversement nécessaire.
            </p>

            <div className="library__grid">
              {library.map((img) => (
                <button
                  key={img.id}
                  type="button"
                  className={`library__item ${
                    libraryImageId === img.id ? "library__item--chosen" : ""
                  } ${img.used ? "library__item--used" : ""}`}
                  aria-pressed={libraryImageId === img.id}
                  title={img.used ? `${img.id} — déjà utilisée` : img.id}
                  onClick={() => {
                    const chosen = libraryImageId === img.id ? null : img.id;
                    // Choisir dans la bibliothèque annule un fichier
                    // éventuellement sélectionné : le serveur donne la priorité
                    // au fichier, l'interface doit dire la même chose.
                    setLibraryImageId(chosen);
                    setImage(null);
                    if (fileInput.current !== null) fileInput.current.value = "";

                    // Pré-remplit le nom, sans jamais écraser une saisie en
                    // cours : l'admin garde la main.
                    if (chosen !== null && img.suggestedName !== null && nameI18n.fr.trim() === "") {
                      setNameI18n(img.suggestedName);
                    }
                  }}
                >
                  <img src={img.url} alt="" loading="lazy" />
                  {img.used && <span className="library__badge">déjà prise</span>}
                </button>
              ))}
            </div>
          </fieldset>
        )}

        <div className="form__row">
          <div className="form__col">
            <label className="form__label" htmlFor="ao-image">
              …ou téléverser une image
            </label>
            <input
              id="ao-image"
              ref={fileInput}
              className="form__input"
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => {
                setImage(e.target.files?.[0] ?? null);
                if (e.target.files?.[0] !== undefined) setLibraryImageId(null);
              }}
            />
          </div>
          <div className="form__col">
            <label className="form__label" htmlFor="ao-order">
              Ordre d'affichage
            </label>
            <input
              id="ao-order"
              className="form__input"
              type="number"
              value={sortOrder}
              onChange={(e) => {
                setSortOrder(Number(e.target.value));
              }}
            />
          </div>
        </div>

        <div className="button-row">
          <button
            type="submit"
            className="button button--primary"
            disabled={nameI18n.fr.trim() === ""}
          >
            {editingId === null ? "Créer" : "Enregistrer"}
          </button>
          {editingId !== null && (
            <button type="button" className="button button--ghost" onClick={reset}>
              Annuler
            </button>
          )}
        </div>
      </form>
    </section>
  );
}
